import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/contexts/auth";
import { finalizarSolicitacao } from "@/http/finalizarSolicitacao";
import { transferirAtendente } from "@/http/transferirAtendente";
import { transferirServico } from "@/http/transferirServico";
import { findAtendentes } from "@/http/findAtendentes";
import { queryClient } from "@/lib/react-query";
import { setores } from "@/constants/setores";
import getServicos from "@/utils/getServicos";
import { EllipsisVertical, LoaderCircle } from "lucide-react";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export function PresetActions({ id, isConcluidos, unidadeId, setorId, servicoId }) {
  const { handleDetalhe, user } = useAuth();
  const navigate = useNavigate();
  const [loadingFinalizar, setLoadingFinalizar] = useState(false);
  const [openFinalizar, setOpenFinalizar] = useState(false);
  const [openTransferir, setOpenTransferir] = useState(false);
  const [loadingTransferir, setLoadingTransferir] = useState(false);
  const [atendentes, setAtendentes] = useState([]);
  const [novoAtendenteId, setNovoAtendenteId] = useState("");
  const [loadingAtendentes, setLoadingAtendentes] = useState(false);
  const [openTransferirServico, setOpenTransferirServico] = useState(false);
  const [loadingTransferirServico, setLoadingTransferirServico] =
    useState(false);
  const [novoSetor, setNovoSetor] = useState("");
  const [novoServico, setNovoServico] = useState("");

  useEffect(() => {
    if (openTransferir) {
      carregarAtendentes();
    }
  }, [openTransferir]);

  useEffect(() => {
    if (openTransferirServico) {
      setNovoSetor(setorId || "");
      setNovoServico(servicoId || "");
    }
  }, [openTransferirServico]);

  async function carregarAtendentes() {
    try {
      setLoadingAtendentes(true);
      const data = await findAtendentes(unidadeId);
      setAtendentes(data);
    } catch (error) {
      toast.error("Erro ao carregar atendentes");
    } finally {
      setLoadingAtendentes(false);
    }
  }

  function handleClickDetalhe() {
    handleDetalhe(id);
    navigate("/admin");
  }

  async function handleFinalizar() {
    try {
      setLoadingFinalizar(true);
      await finalizarSolicitacao(id);
      queryClient.invalidateQueries(["monitoringAtendentes"]);
      queryClient.invalidateQueries(["monitoringEquipe"]);
      queryClient.invalidateQueries(["monitoringFases"]);
      toast.success("Solicitação finalizada com sucesso!");
      setOpenFinalizar(false);
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Erro ao finalizar solicitação",
      );
    } finally {
      setLoadingFinalizar(false);
    }
  }

  async function handleTransferir() {
    if (!novoAtendenteId) {
      toast.error("Selecione um atendente");
      return;
    }

    try {
      setLoadingTransferir(true);
      await transferirAtendente(id, novoAtendenteId);
      queryClient.invalidateQueries(["monitoringAtendentes"]);
      queryClient.invalidateQueries(["monitoringEquipe"]);
      queryClient.invalidateQueries(["monitoringFases"]);
      toast.success("Atendente transferido com sucesso!");
      setOpenTransferir(false);
      setNovoAtendenteId("");
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Erro ao transferir atendente",
      );
    } finally {
      setLoadingTransferir(false);
    }
  }

  async function handleTransferirServico() {
    if (!novoSetor || !novoServico) {
      toast.error("Selecione o setor e o serviço");
      return;
    }

    try {
      setLoadingTransferirServico(true);
      await transferirServico(id, novoSetor, novoServico);
      queryClient.invalidateQueries(["monitoringAtendentes"]);
      queryClient.invalidateQueries(["monitoringEquipe"]);
      queryClient.invalidateQueries(["monitoringFases"]);
      toast.success("Serviço transferido com sucesso!");
      setOpenTransferirServico(false);
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Erro ao transferir serviço",
      );
    } finally {
      setLoadingTransferirServico(false);
    }
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="cursor-pointer items-center justify-center whitespace-nowrap rounded-sm text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 hover:bg-accent hover:text-accent-foreground flex h-6 w-6 p-0 data-[state=open]:bg-muted">
            <EllipsisVertical />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            onClick={handleClickDetalhe}
            className="cursor-pointer"
          >
            Ver detalhes
          </DropdownMenuItem>
          <DropdownMenuItem
            className="cursor-pointer"
            onSelect={(e) => {
              e.preventDefault();
              setOpenTransferir(true);
            }}
          >
            Transferir atendente
          </DropdownMenuItem>
          <DropdownMenuItem
            className="cursor-pointer"
            onSelect={(e) => {
              e.preventDefault();
              setOpenTransferirServico(true);
            }}
          >
            Transferir serviço
          </DropdownMenuItem>
          {!isConcluidos && (
            <DropdownMenuItem
              className="cursor-pointer text-red-600"
              onSelect={(e) => {
                e.preventDefault();
                setOpenFinalizar(true);
              }}
            >
              Encerrar
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
      <ModalFinalizar
        open={openFinalizar}
        setOpen={setOpenFinalizar}
        handleFinalizar={handleFinalizar}
        loadingFinalizar={loadingFinalizar}
      />
      <ModalTransferir
        open={openTransferir}
        setOpen={setOpenTransferir}
        handleTransferir={handleTransferir}
        loadingTransferir={loadingTransferir}
        atendentes={atendentes}
        loadingAtendentes={loadingAtendentes}
        novoAtendenteId={novoAtendenteId}
        setNovoAtendenteId={setNovoAtendenteId}
      />
      <ModalTransferirServico
        open={openTransferirServico}
        setOpen={setOpenTransferirServico}
        handleTransferirServico={handleTransferirServico}
        loadingTransferirServico={loadingTransferirServico}
        novoSetor={novoSetor}
        setNovoSetor={setNovoSetor}
        novoServico={novoServico}
        setNovoServico={setNovoServico}
      />
    </>
  );
}

function ModalFinalizar({ open, setOpen, handleFinalizar, loadingFinalizar }) {
  return (
    <AlertDialog open={open} onOpenChange={setOpen} modal={false}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Você tem certeza absoluta?</AlertDialogTitle>
          <AlertDialogDescription>
            Esta ação não pode ser desfeita. Você encerrará o atendimento e não
            será possível que o atendente envie mais nenhuma resposta.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel
            className="cursor-pointer"
            onClick={() => setOpen(false)}
          >
            Cancelar
          </AlertDialogCancel>
          <button
            onClick={handleFinalizar}
            disabled={loadingFinalizar}
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-[color,box-shadow] disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive bg-primary text-primary-foreground shadow-xs hover:bg-primary/90 h-9 px-4 py-2 has-[>svg]:px-3 cursor-pointer"
          >
            {loadingFinalizar && (
              <LoaderCircle className="animate-spin text-slate-50 size-5" />
            )}
            {loadingFinalizar ? "Encerrando..." : "Encerrar"}
          </button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function ModalTransferir({
  open,
  setOpen,
  handleTransferir,
  loadingTransferir,
  atendentes,
  loadingAtendentes,
  novoAtendenteId,
  setNovoAtendenteId,
}) {
  return (
    <AlertDialog open={open} onOpenChange={setOpen} modal={false}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Transferir Atendente</AlertDialogTitle>
          <AlertDialogDescription>
            Selecione o novo atendente para esta solicitação.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="py-4">
          <Select value={novoAtendenteId} onValueChange={setNovoAtendenteId}>
            <SelectTrigger disabled={loadingAtendentes}>
              <SelectValue placeholder="Selecione um atendente" />
            </SelectTrigger>
            <SelectContent>
              {atendentes.map((atendente) => (
                <SelectItem key={atendente._id} value={atendente._id}>
                  {atendente.nome}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel
            className="cursor-pointer"
            onClick={() => setOpen(false)}
          >
            Cancelar
          </AlertDialogCancel>
          <button
            onClick={handleTransferir}
            disabled={loadingTransferir || !novoAtendenteId}
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-[color,box-shadow] disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive bg-primary text-primary-foreground shadow-xs hover:bg-primary/90 h-9 px-4 py-2 has-[>svg]:px-3 cursor-pointer"
          >
            {loadingTransferir && (
              <LoaderCircle className="animate-spin text-slate-50 size-5" />
            )}
            {loadingTransferir ? "Transferindo..." : "Transferir"}
          </button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

function ModalTransferirServico({
  open,
  setOpen,
  handleTransferirServico,
  loadingTransferirServico,
  novoSetor,
  setNovoSetor,
  novoServico,
  setNovoServico,
}) {
  const servicos = getServicos(novoSetor);

  function handleSetorChange(value) {
    setNovoSetor(value);
    setNovoServico("");
  }

  return (
    <AlertDialog open={open} onOpenChange={setOpen} modal={false}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Transferir Serviço</AlertDialogTitle>
          <AlertDialogDescription>
            Selecione o setor e o serviço correto para esta solicitação.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="py-4 flex flex-col gap-3">
          <Select value={novoSetor} onValueChange={handleSetorChange}>
            <SelectTrigger>
              <SelectValue placeholder="Selecione um setor" />
            </SelectTrigger>
            <SelectContent>
              {setores.options.map((setor) => (
                <SelectItem key={setor.id} value={setor.id}>
                  {setor.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select
            value={novoServico}
            onValueChange={setNovoServico}
            disabled={!novoSetor || servicos.length === 0}
          >
            <SelectTrigger>
              <SelectValue
                placeholder={
                  novoSetor && servicos.length === 0
                    ? "Nenhum serviço disponível"
                    : "Selecione um serviço"
                }
              />
            </SelectTrigger>
            <SelectContent>
              {servicos.map((servico) => (
                <SelectItem key={servico.id} value={servico.id}>
                  {servico.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel
            className="cursor-pointer"
            onClick={() => setOpen(false)}
          >
            Cancelar
          </AlertDialogCancel>
          <button
            onClick={handleTransferirServico}
            disabled={loadingTransferirServico || !novoSetor || !novoServico}
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-[color,box-shadow] disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive bg-primary text-primary-foreground shadow-xs hover:bg-primary/90 h-9 px-4 py-2 has-[>svg]:px-3 cursor-pointer"
          >
            {loadingTransferirServico && (
              <LoaderCircle className="animate-spin text-slate-50 size-5" />
            )}
            {loadingTransferirServico ? "Transferindo..." : "Transferir"}
          </button>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
